import { Component, computed, Injector, input, OnInit, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { TextInputComponent } from '../../../../../../../shared/components/forms/text-input/text-input.component';
import { TextSearchComponent } from '../../../../../../../shared/components/forms/text-search/text-search.component';
import { TelephoneLinkComponent } from '../../../../../../../shared/components/telephone-link/telephone-link.component';
import { AnimateOpenDirective } from '../../../../../../../shared/directives/animate-open.directive';
import { templateDrivenFormsViewProviders } from '../../../../../../../shared/directives/form.directive';
import { StringifyPipe } from '../../../../../../../shared/pipes/stringify';
import { getBrandImagesForManufacturer } from '../../../../../../../shared/utils/get-model-number-image';
import { OrbitFormClaimService } from '../../../../services/orbit-form-claim.service';
import { getErrorsFromSubmission } from '../../pipes/error-subscription';
import { formValidationGenerator } from '../../pipes/form-validation';

@Component({
  selector: 'app-product-number-code',
  imports: [
    FormsModule,
    TextInputComponent,
    TextSearchComponent,
    AnimateOpenDirective,
    StringifyPipe,
    TelephoneLinkComponent,
  ],
  templateUrl: './product-number-code.component.html',
  styleUrl: './product-number-code.component.scss',
  viewProviders: [templateDrivenFormsViewProviders],
})
export class ProductNumberCodeComponent implements OnInit {
  formSubmitted = input<boolean>(false);
  formValue = input<Record<string, any>>({});
  formValid = output<boolean>();

  errors = signal<Record<string, Record<string, any>>>({});
  panelExpanded = signal(false);
  callPanelExpanded = signal(false);
  pncSearchError = signal(false);
  pncNoResults = signal(false);

  private readonly viewModel = computed(() => {
    const { applianceCode, manufacturer } = this.orbitFormClaimService.state.claimData.bundle;
    const { applianceSuperCategory } = this.orbitFormClaimService.state.claimData.superCategory;
    return {
      fields: this.getConfigForEachStep(),
      formSubmitted: this.formSubmitted(),
      formValue: this.formValue(),
      errors: this.errors(),
      submittingResource: this.orbitFormClaimService.formSubmission,
      panelExpanded: this.panelExpanded(),
      callPanelExpanded: this.callPanelExpanded(),
      pncSearchError: this.pncSearchError(),
      pncNoResults: this.pncNoResults(),
      brandImage: getBrandImagesForManufacturer(applianceCode, applianceSuperCategory, manufacturer),
    };
  });

  get vm() {
    return this.viewModel();
  }

  constructor(
    private readonly orbitFormClaimService: OrbitFormClaimService,
    private readonly injector: Injector
  ) {}

  private getConfigForEachStep(): Record<string, any> {
    return (this.orbitFormClaimService.state.claimData.mandatoryData.oEMApplianceData ?? [])
      .filter((item: any) => {
        if (item.dataType === 'PNCNumber') return true;
        if (item.dataType === 'MLCode' || item.fieldIdentifier === 'MLCode') return true;
        return false;
      })
      .reduce((acc: Record<string, any>, item: any) => {
        const key = item.dataType ?? item.fieldIdentifier;
        return { ...acc, [key]: item };
      }, {});
  }

  ngOnInit(): void {
    this.formValid.emit(false);
    this.handleFormValidation();
    this.handleAPIErrors();
  }

  toggleCallPanel(): void {
    this.callPanelExpanded.set(!this.callPanelExpanded());
    setTimeout(() => window.dispatchEvent(new Event('resize')));
  }

  onPncSearchError(): void {
    this.pncSearchError.set(true);
  }

  onPncNoResults(): void {
    this.pncNoResults.set(true);
  }

  onPncLoadingData(): void {
    this.pncSearchError.set(false);
    this.pncNoResults.set(false);
  }

  handleAPIErrors(): void {
    getErrorsFromSubmission(this.vm.submittingResource, this.injector, (attempts, error) => {
      console.log({ error });
    });
  }

  private readonly ML_CODE_REGEX = /^\d{2}$/;

  handleFormValidation(): void {
    const requiredFields = Object.values(this.getConfigForEachStep()).map((item: any) => item.fieldIdentifier);
    const mlCodeFieldId = this.getConfigForEachStep()['MLCode']?.fieldIdentifier;

    toObservable(this.formValue, { injector: this.injector })
      .pipe(
        formValidationGenerator({}, [
          (value) => {
            const anyMissing = requiredFields.some((field) => !value[field]);

            return anyMissing ? { required: true } : null;
          },
          (value) => {
            const mlValue = mlCodeFieldId ? value[mlCodeFieldId] : undefined;
            if (mlValue && !this.ML_CODE_REGEX.test(mlValue)) {
              return { mlCodeInvalid: true };
            }
            return null;
          },
        ])
      )
      .subscribe({
        next: ({ isFormValid, errors }) => {
          this.errors.set(errors);
          this.formValid.emit(isFormValid);
        },
      });
  }
}
